// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Form, TextArea, Select, Option, Radio, RadioGroup,
} from 'informed';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { addProblem } from '../../../actions/problemActions';

import ContentBox from '../../ContentBox.jsx';
import PageHeader from '../../PageHeader.jsx';
import { PrimaryButton, Button } from '../../Buttons.jsx';
import { PrimaryText, Label } from '../../Typography.jsx';
import NumericText from '../../NumericText.jsx';
import ValidateText from '../../ValidateText.jsx';
import DateText from '../../DateText.jsx';
import DatetimeText from '../../DatetimeText.jsx';
import Typeahead from '../../Typeahead.jsx';
import ProblemMedicationTable from '../progressNote/ProblemMedicationTable.jsx';
import ProblemInterventionTable from '../progressNote/ProblemInterventionTable.jsx';

type Props = {
  activeAccount: Object,
  activePatientId: string,
  addProblem: Function,
  problem: Object,
  problemIdToMeds: Object,
  problemIdToInterventions: Object,
}

type State = {
  createdProblem: boolean,
}

class NewDiagnosisPageComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      createdProblem: false,
    };
  }

  formApi: {
    getState: Function,
    getValue: Function,
  };

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
  }

  /* eslint-disable no-param-reassign */
  transformData(formValues) {
    if (formValues.problem) {
      formValues.problem.onsetDate = moment(formValues.problem.onsetDate, 'DD-MM-YYYY').toISOString();
    }
    formValues.date = moment(formValues.date, 'DD-MM-YYYY, HH:mm').toISOString();
    if (formValues.vitals && formValues.vitals.temperatureUnit === 'c') {
      formValues.vitals.temperature = (formValues.vitals.temperature * 9 / 5 + 32).toFixed(1);
    }
    const { activeAccount } = this.props;
    formValues.editor = `${activeAccount.name}, ${activeAccount.credential}`;
    formValues.editDate = moment().toISOString();
  }

  handleSave = () => {
    const { invalid, values, touched } = this.formApi.getState();
    if (invalid || !Object.keys(touched).length) {
      return;
    }
    this.transformData(values);
    this.props.addProblem(this.props.activePatientId, values);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.problem.status === 'LOADING' && this.props.problem.status === 'SUCCESS') {
      this.setState({
        createdProblem: true,
      });
    }
  }

  // Note: this component consists of code copy-pasted/adapted from other components
  //   EditSymptomAnalysis
  //   EditVitalSigns
  render() {
    return (
      <div>
        <PageHeader title="Add New Diagnosis" />
        <Form getApi={this.setFormApi} autoComplete='off'>
          <ContentBox title='Symptom Analysis' collapsable>
            <div>
              <PrimaryText>Chief Complaint</PrimaryText><br />
              <TextArea field='symptomAnalysis.complaint' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Onset</PrimaryText><br />
              <TextArea field='symptomAnalysis.onset' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Location</PrimaryText><br />
              <TextArea field='symptomAnalysis.location' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Duration</PrimaryText><br />
              <TextArea field='symptomAnalysis.duration' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Characteristics</PrimaryText><br />
              <TextArea field='symptomAnalysis.characteristics' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Aggravating Factors</PrimaryText><br />
              <TextArea field='symptomAnalysis.aggravatingFactors' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Relieving Factors</PrimaryText><br />
              <TextArea field='symptomAnalysis.relievingFactors' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Treatment</PrimaryText><br />
              <TextArea field='symptomAnalysis.treatment' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Associated Signs and Symptoms</PrimaryText><br />
              <TextArea field='symptomAnalysis.signsSymptoms' disabled={this.state.createdProblem} />
            </div>
            <div>
              <PrimaryText>Notes</PrimaryText><br />
              <TextArea field='symptomAnalysis.notes' disabled={this.state.createdProblem} />
            </div>
          </ContentBox>
          <ContentBox title="Vital Signs" collapsable>
            <table>
              <tbody>
                <tr>
                  <td><PrimaryText>Temperature </PrimaryText></td>
                  <td>
                    <NumericText field='vitals.temperature' size={7} />
                    <Select field='vitals.temperatureUnit' disabled={this.state.createdProblem}>
                      <Option value='f'>°F</Option>
                      <Option value='c'>°C</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td><Label>Site</Label></td>
                  <td>
                    <Select field='vitals.temperatureSite' disabled={this.state.createdProblem}>
                      <Option>--</Option>
                      <Option value='Oral'>Oral</Option>
                      <Option value='Tympanic'>Tympanic</Option>
                      <Option value='Axillary'>Axillary</Option>
                      <Option value='Rectal'>Rectal</Option>
                      <Option value='Other (see notes)'>Other (see notes)</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td><PrimaryText>Respiration Rate </PrimaryText></td>
                  <td><NumericText field='vitals.rr' size={7} disabled={this.state.createdProblem} /> breaths per min</td>
                </tr>
                <tr>
                  <td><PrimaryText>Heart Rate </PrimaryText></td>
                  <td><NumericText field='vitals.hr' size={7} disabled={this.state.createdProblem} /> bpm</td>
                </tr>
                <tr>
                  <td><Label>Site</Label></td>
                  <td>
                    <Select field='vitals.hrSite' disabled={this.state.createdProblem}>
                      <Option>--</Option>
                      <Option value='Apical'>Apical</Option>
                      <Option value='Left Radial'>Left Radial</Option>
                      <Option value='Right Radial'>Right Radial</Option>
                      <Option value='Other (see notes)'>Other (see notes)</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td><PrimaryText>Blood Pressure </PrimaryText></td>
                  <td>
                    <ValidateText
                      placeholder='#/#'
                      field='vitals.bp'
                      regex={/^(\d+\/\d+)?$/}
                      errorText={'Please enter valid BP, (e.g. 120/80)'}
                      size={7}
                      disabled={this.state.createdProblem}
                    />
                  </td>
                </tr>
                <tr>
                  <td><Label>Site</Label></td>
                  <td>
                    <Select field='vitals.bpSite' disabled={this.state.createdProblem}>
                      <Option>--</Option>
                      <Option value='Left Brachial'>Left Brachial</Option>
                      <Option value='Right Brachial'>Right Brachial</Option>
                      <Option value='Left Leg'>Left Leg</Option>
                      <Option value='Right Leg'>Right Leg</Option>
                      <Option value='Other (see notes)'>Other (see notes)</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td><Label>Position</Label></td>
                  <td>
                    <Select field='vitals.bpPosition' disabled={this.state.createdProblem}>
                      <Option>--</Option>
                      <Option value='Sitting'>Sitting</Option>
                      <Option value='Standing'>Standing</Option>
                      <Option value='Lying'>Lying</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td><PrimaryText>SpO2 </PrimaryText></td>
                  <td><NumericText field='vitals.spo2' size={8} disabled={this.state.createdProblem} /> %</td>
                </tr>
                <tr>
                  <td><Label>Site</Label></td>
                  <td>
                    <Select field='vitals.spo2Site' disabled={this.state.createdProblem}>
                      <Option>--</Option>
                      <Option value='Left Finger'>Left Finger</Option>
                      <Option value='Right Finger'>Right Finger</Option>
                      <Option value='Left Toe'>Left Toe</Option>
                      <Option value='Right Toe'>Right Toe</Option>
                      <Option value='Other (see notes)'>Other (see notes)</Option>
                    </Select>
                  </td>
                </tr>
              </tbody>
            </table>
            <div>
              <PrimaryText>Notes</PrimaryText><br />
              <TextArea field='vitals.notes' disabled={this.state.createdProblem} />
            </div>
          </ContentBox>
          <ContentBox title="Diagnosis" collapsable>
            <table>
              <tbody>
                <tr>
                  <td><PrimaryText>Date Of Onset </PrimaryText></td>
                  <td>
                    <DateText field='problem.onsetDate' item={{}} disabled={this.state.createdProblem} />
                  </td>
                </tr>
                <tr>
                  <td><PrimaryText>Diagnosis</PrimaryText></td>
                  <td>
                    <Typeahead field='problem.diagnosis' endpoint='diagnosis' disabled={this.state.createdProblem} />
                  </td>
                </tr>
                <tr>
                  <td><PrimaryText>Type</PrimaryText></td>
                  <td>
                    <RadioGroup field='problemType' initialValue='acute' disabled={this.state.createdProblem}>
                      <Radio value='acute' disabled={this.state.createdProblem} /> Acute{'  '}
                      <Radio value='chronic' disabled={this.state.createdProblem} /> Chronic
                    </RadioGroup>
                  </td>
                </tr>
              </tbody>
            </table>
            <PrimaryText>Details</PrimaryText><br />
            <TextArea field='problem.details' disabled={this.state.createdProblem} /><br />
            <PrimaryText>Notes</PrimaryText><br />
            <TextArea field='problem.notes' disabled={this.state.createdProblem} />
          </ContentBox>
          {!this.state.createdProblem
            && <ContentBox>
              <PrimaryText>Exam Date: </PrimaryText>
              <DatetimeText field='date' /><br /><br />
              <PrimaryButton onClick={this.handleSave}>Create Problem and Continue</PrimaryButton>
              <Link to={`/patient/${this.props.activePatientId}/currentProblems`}><Button>Cancel</Button></Link>
            </ContentBox>
          }
        </Form>
        <div>
          {this.state.createdProblem
            && <div>
              <ContentBox title="Medications">
                <ProblemMedicationTable
                  problemId={this.props.problem.data.problemId}
                  medications={this.props.problemIdToMeds[this.props.problem.data.problemId] || []}
                  acute={this.formApi.getValue('problemType') === 'acute'}
                  chronic={this.formApi.getValue('problemType') === 'chronic'}
                />
              </ContentBox>
              <ContentBox title="Interventions">
                <ProblemInterventionTable
                  problemId={this.props.problem.data.problemId}
                  interventions={this.props.problemIdToInterventions[this.props.problem.data.problemId] || []}
                  acute={this.formApi.getValue('problemType') === 'acute'}
                  chronic={this.formApi.getValue('problemType') === 'chronic'}
                />
              </ContentBox>
            </div>}
          {this.state.createdProblem
            && <ContentBox>
              <Link to={`/patient/${this.props.activePatientId}/currentProblems`}><PrimaryButton>Go Back to Problem List</PrimaryButton></Link>
            </ContentBox>
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  activeAccount: state.account.activeAccount,
  activePatientId: state.patient.data.activePatientId,
  problem: state.problem,
  problemIdToMeds: state.patient.data.problemIdToMeds,
  problemIdToInterventions: state.patient.data.problemIdToInterventions,
});

const mapDispatchToProps = dispatch => ({
  addProblem: (patientId, values) => dispatch(addProblem(patientId, values)),
});

const NewDiagnosisPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(NewDiagnosisPageComponent);

export default NewDiagnosisPage;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/problem/NewDiagnosisPage.jsx